import { useQuery } from '@tanstack/react-query';
import { useMatches, useSearchParams } from 'react-router';

import { useFundQuery } from '@entities/fund';
import { portfolioDetailQueryOptions } from '@entities/portfolio';
import { SCREEN_TITLE } from '@shared/config';
import { useCurrentScreenName } from '@shared/lib/screen-history';

import type { ScreenInfo } from './screen-handle';
import { screenParent } from './screen-tree';

/**
 * BackBar 의 글자 · 갈 곳(DD-52). 갈 곳 = 화면 트리의 상위 화면(screen-tree.ts), 브라우저 기록이 아니다.
 * 글자 = "상위 - 현재". 상위가 펀드 · 회사면 그 이름(받는 중이면 화면 이름), 현재는 화면이 알린 대상 이름.
 * 헤더 · 계정 메뉴 화면(menuRoot)은 자기 이름만(Figma). 이름 조회가 실패해도 머리는 그대로 — 화면 이름으로 둔다.
 */
export function useBackBar(screen: ScreenInfo | null): { title: string; path: string } | null {
  const matches = useMatches();
  const [search] = useSearchParams();
  const params = (matches.at(-1)?.params ?? {}) as Record<string, string | undefined>;
  const parent = screen ? screenParent(screen.id, { params, search }) : null;
  const current = useCurrentScreenName(screen ? SCREEN_TITLE[screen.id] : '');

  const fund = useFundQuery(parent?.kind === 'fund' ? parent.id : 0, {
    enabled: parent?.kind === 'fund' && !screen?.menuRoot,
    throwOnError: false,
  });
  const portfolio = useQuery({
    ...portfolioDetailQueryOptions(parent?.kind === 'portfolio' ? parent.id : 0),
    enabled: parent?.kind === 'portfolio' && !screen?.menuRoot,
    throwOnError: false,
  });

  if (!screen || !parent) return null;
  if (screen.menuRoot) return { title: current, path: parent.path };
  const parentName =
    parent.kind === 'fund'
      ? (fund.data?.name ?? SCREEN_TITLE.S05)
      : parent.kind === 'portfolio'
        ? (portfolio.data?.name ?? SCREEN_TITLE.S08)
        : SCREEN_TITLE[parent.id];
  return { title: `${parentName} - ${current}`, path: parent.path };
}
